// Logic behind the "Generate All" button, kept out of SocialHub.jsx so it can be
// tested and so the button can report what actually happened instead of going
// quiet when it finishes (or when one platform fails).

// Same limits Approve & Auto-Produce uses. X is special: only its opening post
// has a limit, the rest of the thread is left alone.
export const CHAR_LIMITS = { bs: 300, th: 500, ms: 500 };
export const TW_OPENER_LIMIT = 280;

// Brings generated text within its platform's limit. `trim(limit, text)` is the
// rewrite call and returns the shorter text, or null on failure. Never throws.
// overBy > 0 means the text is STILL too long after trying, so the user is told.
export async function fitToPlatform(platId, text, trim) {
  let out = text;
  let trimmed = false;
  const limit = CHAR_LIMITS[platId];
  if (limit && out.length > limit) {
    const shorter = await trim(limit, out);
    if (shorter) { out = shorter; trimmed = true; }
    if (out.length > limit) return { text: out, trimmed, limit, overBy: out.length - limit };
  }
  if (platId === 'tw') {
    const parts = out.split(/\n\s*\n/);
    if (parts[0] && parts[0].length > TW_OPENER_LIMIT) {
      const shorter = await trim(TW_OPENER_LIMIT, parts[0]);
      if (shorter) { parts[0] = shorter; out = parts.join('\n\n'); trimmed = true; }
      if (parts[0].length > TW_OPENER_LIMIT) {
        return { text: out, trimmed, limit: TW_OPENER_LIMIT, overBy: parts[0].length - TW_OPENER_LIMIT };
      }
    }
  }
  return { text: out, trimmed, limit: limit || null, overBy: 0 };
}

// A failed generation leaves "Error: ..." in the card. That is not content.
export const isErrorText = (text) => typeof text === 'string' && text.startsWith('Error: ');
export const hasRealContent = (text) => !!text && !isErrorText(text);

// items: [{ id, label, status, trimmed?, error?, saveStatus?, overBy?, limit? }]
// status: 'ok' (generated + saved) | 'kept' (already had text) | 'busy' (already
//         generating) | 'failed' (no text came back) | 'unsaved' (text exists
//         but the server refused to store it)
export function summarizeGenerateAll(items) {
  const total = items.length;
  const lines = [];
  let broken = 0;
  let long = 0;
  for (const it of items) {
    if (it.status === 'failed') {
      broken += 1;
      lines.push(`${it.label}: generation failed${it.error ? ` (${it.error})` : ''}. Click Generate on its card to retry.`);
    } else if (it.status === 'unsaved') {
      broken += 1;
      lines.push(`${it.label}: written, but NOT saved to the server${it.saveStatus ? ` (HTTP ${it.saveStatus})` : ''}. It will be lost if you reload.`);
    } else if (it.status === 'busy') {
      broken += 1;
      lines.push(`${it.label}: was already generating, so it was skipped.`);
    }
    if (it.overBy > 0) {
      long += 1;
      lines.push(`${it.label}: still ${it.limit + it.overBy} characters after trimming (limit ${it.limit}). Shorten it before publishing.`);
    }
  }
  const made = items.filter((i) => i.status === 'ok').length;
  const trimmedCount = items.filter((i) => i.trimmed).length;
  const tone = broken > 0 ? 'error' : long > 0 ? 'warn' : 'ok';
  const attention = broken + long;
  const headline = tone === 'ok'
    ? `All ${total} platforms are ready and saved.`
    : `${attention} of ${total} platforms need your attention.`;
  const facts = [];
  if (made) facts.push(`${made} generated`);
  const kept = items.filter((i) => i.status === 'kept').length;
  if (kept) facts.push(`${kept} already had text`);
  if (trimmedCount) facts.push(`${trimmedCount} shortened to fit`);
  const next = 'Nothing has been sent to Media Studio yet. Next: Approve & Auto-Produce (under "More options") sends it and starts the images and videos.';
  return { tone, headline, facts: facts.join(', '), lines, next };
}
